// src/services/trinketService.ts
import { logger } from '../utils/logger';

import { supabase } from './supabase';
import type { TrinketTemplate, UserTrinket } from '../components/games/diceMatch/trinketTypes';

/**
 * Get all available trinket templates
 */
export async function getTrinketTemplates(): Promise<TrinketTemplate[]> {
  try {
    const { data, error } = await supabase
      .from('dice_match_trinket_templates')
      .select('*')
      .eq('is_active', true)
      .order('rarity', { ascending: true })
      .order('name', { ascending: true });

    if (error) {
      logger.error('Error fetching trinket templates:', error);
      return [];
    }

    // Map database fields to include aliases
    return (data || []).map(template => ({
      ...template,
      icon: template.icon_emoji,
      meta_cost: template.cost_meta_currency || 0,
    }));
  } catch (error) {
    logger.error('Error in getTrinketTemplates:', error);
    return [];
  }
}

/**
 * Get user's trinkets
 */
export async function getUserTrinkets(): Promise<UserTrinket[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('dice_match_trinkets')
      .select(`
        *,
        template:dice_match_trinket_templates(*)
      `)
      .eq('user_id', user.id)
      .order('acquired_at', { ascending: false });

    if (error) {
      logger.error('Error fetching user trinkets:', error);
      return [];
    }

    return (data || []).map((item: any) => ({
      ...item,
      template: item.template,
    }));
  } catch (error) {
    logger.error('Error in getUserTrinkets:', error);
    return [];
  }
}

/**
 * Get equipped trinkets for a game
 */
export async function getEquippedTrinkets(): Promise<UserTrinket[]> {
  try {
    const trinkets = await getUserTrinkets();
    return trinkets.filter(t => t.is_equipped && t.template);
  } catch (error) {
    logger.error('Error in getEquippedTrinkets:', error);
    return [];
  }
}

/**
 * Purchase a trinket with meta currency
 */
export async function purchaseTrinket(trinketId: string, cost: number): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Check if user has enough meta currency
    const { data: meta } = await supabase
      .from('dice_match_meta_progression')
      .select('meta_currency')
      .eq('user_id', user.id)
      .single();

    if (!meta || meta.meta_currency < cost) {
      logger.error('Not enough meta currency');
      return false;
    }

    // Deduct currency and add trinket
    const { error: updateError } = await supabase
      .from('dice_match_meta_progression')
      .update({ meta_currency: meta.meta_currency - cost })
      .eq('user_id', user.id);

    if (updateError) {
      logger.error('Error updating meta currency:', updateError);
      return false;
    }

    // Add trinket (upsert to increment quantity if already owned)
    const { data: existing } = await supabase
      .from('dice_match_trinkets')
      .select('quantity')
      .eq('user_id', user.id)
      .eq('trinket_id', trinketId)
      .single();

    if (existing) {
      // Increment quantity
      const { error: incError } = await supabase
        .from('dice_match_trinkets')
        .update({ quantity: existing.quantity + 1 })
        .eq('user_id', user.id)
        .eq('trinket_id', trinketId);

      if (incError) {
        logger.error('Error incrementing trinket quantity:', incError);
        return false;
      }
    } else {
      // Add new trinket
      const { error: insertError } = await supabase
        .from('dice_match_trinkets')
        .insert({
          user_id: user.id,
          trinket_id: trinketId,
          quantity: 1,
          is_equipped: false,
        });

      if (insertError) {
        logger.error('Error adding trinket:', insertError);
        return false;
      }
    }

    return true;
  } catch (error) {
    logger.error('Error in purchaseTrinket:', error);
    return false;
  }
}

/**
 * Equip/unequip a trinket
 */
export async function toggleTrinketEquipped(trinketId: string, equipped: boolean): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { error } = await supabase
      .from('dice_match_trinkets')
      .update({ is_equipped: equipped })
      .eq('user_id', user.id)
      .eq('trinket_id', trinketId);

    if (error) {
      logger.error('Error toggling trinket equipped:', error);
      return false;
    }

    return true;
  } catch (error) {
    logger.error('Error in toggleTrinketEquipped:', error);
    return false;
  }
}

/**
 * Use a temporary trinket (decrements quantity)
 */
export async function consumeTrinket(trinketId: string, gameId?: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Get current quantity
    const { data: trinket } = await supabase
      .from('dice_match_trinkets')
      .select('quantity')
      .eq('user_id', user.id)
      .eq('trinket_id', trinketId)
      .single();

    if (!trinket || trinket.quantity <= 0) {
      logger.error('Trinket not found or quantity is 0');
      return false;
    }

    // Decrement quantity (or delete if 0)
    if (trinket.quantity === 1) {
      const { error: deleteError } = await supabase
        .from('dice_match_trinkets')
        .delete()
        .eq('user_id', user.id)
        .eq('trinket_id', trinketId);

      if (deleteError) {
        logger.error('Error deleting trinket:', deleteError);
        return false;
      }
    } else {
      const { error: updateError } = await supabase
        .from('dice_match_trinkets')
        .update({
          quantity: trinket.quantity - 1,
          last_used_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .eq('trinket_id', trinketId);

      if (updateError) {
        logger.error('Error updating trinket quantity:', updateError);
        return false;
      }
    }

    // Log usage
    await supabase
      .from('dice_match_trinket_usage')
      .insert({
        user_id: user.id,
        trinket_id: trinketId,
        game_id: gameId || null,
      });

    return true;
  } catch (error) {
    logger.error('Error in consumeTrinket:', error);
    return false;
  }
}

/**
 * Award a trinket (from drops, achievements, etc.)
 */
export async function awardTrinket(trinketId: string, quantity: number = 1): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Check if user already has this trinket
    const { data: existing } = await supabase
      .from('dice_match_trinkets')
      .select('quantity')
      .eq('user_id', user.id)
      .eq('trinket_id', trinketId)
      .single();

    if (existing) {
      // Increment quantity
      const { error } = await supabase
        .from('dice_match_trinkets')
        .update({ quantity: existing.quantity + quantity })
        .eq('user_id', user.id)
        .eq('trinket_id', trinketId);

      if (error) {
        logger.error('Error incrementing trinket:', error);
        return false;
      }
    } else {
      // Add new trinket
      const { error } = await supabase
        .from('dice_match_trinkets')
        .insert({
          user_id: user.id,
          trinket_id: trinketId,
          quantity,
          is_equipped: false,
        });

      if (error) {
        logger.error('Error awarding trinket:', error);
        return false;
      }
    }

    return true;
  } catch (error) {
    logger.error('Error in awardTrinket:', error);
    return false;
  }
}

/**
 * Random trinket drop based on drop weights
 */
export async function rollRandomTrinket(): Promise<string | null> {
  try {
    // Get all droppable trinkets
    const { data: templates } = await supabase
      .from('dice_match_trinket_templates')
      .select('id, drop_weight')
      .eq('is_active', true)
      .gt('drop_weight', 0);

    if (!templates || templates.length === 0) {
      return null;
    }

    // Calculate total weight
    const totalWeight = templates.reduce((sum, t) => sum + t.drop_weight, 0);

    // Roll random number
    const roll = Math.random() * totalWeight;

    // Find which trinket was rolled
    let currentWeight = 0;
    for (const template of templates) {
      currentWeight += template.drop_weight;
      if (roll < currentWeight) {
        return template.id;
      }
    }

    return null;
  } catch (error) {
    logger.error('Error in rollRandomTrinket:', error);
    return null;
  }
}
